import { useState, useEffect } from 'react';
import ExpandableSection from '@components/ExpandableSection/ExpandableSection';
import useToast from '@contexts/useToast';
import { ToastType } from '@customTypes/toast';
import { confirmFaxVerification, requestFaxVerification, getClinicDetails, updateClinicDetails } from '@store/actions';
import { IClinic } from '@customTypes/clinician';
import { Spinner } from 'react-bootstrap';
import ClinicDetailsView from './ClinicDetailsView';
import ClinicForm from './ClinicForm';
import { useSettings } from '@hooks';
import './ClinicDetails.scss';

interface ClinicDetailsProps {
  clinicId: string;
  clinicName: string;
}

export default function ClinicDetails({ clinicId, clinicName }: ClinicDetailsProps) {
  const { addToast } = useToast();
  const { isNatera } = useSettings();
  const [isEditing, setIsEditing] = useState(false);
  const [clinicData, setClinicData] = useState<IClinic | null>(null);

  // Verification State
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [verificationMessage, setVerificationMessage] = useState('');

  useEffect(() => {
    let isMounted = true;
    getClinicDetails(clinicId)
      .then(data => {
        if (isMounted) {
          setClinicData(data);
        }
      })
      .catch(console.error);

    return () => {
      isMounted = false;
    };
  }, [clinicId]);

  const sendVerificationCode = async () => {
    try {
      await requestFaxVerification(clinicId);
      setVerificationStatus('idle');
      addToast({
        headerTitle: 'Verification Code Sent',
        type: ToastType.DEFAULT,
        variant: 'success',
        delay: 10000,
        autohide: true,
      });
    } catch (error: any) {
      setVerificationStatus('error');
      addToast({
        headerTitle:
          error?.response?.data?.detail ||
          error?.response?.data?.title ||
          'Failed to send verification code. Please try again.',
        type: ToastType.DEFAULT,
        variant: 'danger',
      });
    }
  };

  const handleSave = async (formData: IClinic) => {
    try {
      const payload: Partial<IClinic> = {
        name: formData.name,
        phone: formData.phone || '',
        fax: formData.fax || '',
        is_fax_enabled: formData.is_fax_enabled,
        address: formData.address
          ? {
              ...formData.address,
              street_address_2: formData.address.street_address_2 || '',
              country: formData.address.country || '',
            }
          : undefined,
      };

      const updatedClinic = await updateClinicDetails(clinicId, payload);
      setClinicData(updatedClinic);
      setIsEditing(false);

      const faxEnabled = updatedClinic.is_fax_enabled ?? false;
      const faxVerified = updatedClinic.fax_number_verified ?? false;
      const faxChanged = formData.fax !== clinicData?.fax;
      const faxEnabledChanged = faxEnabled && !clinicData?.is_fax_enabled;

      if (faxEnabled && !faxVerified && (faxChanged || faxEnabledChanged)) {
        sendVerificationCode();
      } else {
        addToast({
          headerTitle: 'Organization details updated successfully',
          type: ToastType.DEFAULT,
          variant: 'success',
        });
      }
    } catch (error) {
      console.error(error);
      addToast({
        headerTitle: 'Failed to update organization details',
        type: ToastType.DEFAULT,
        variant: 'danger',
      });
      throw error; // Let Formik handle the submission state if needed
    }
  };

  const submitVerification = async () => {
    setVerificationStatus('pending');

    try {
      await confirmFaxVerification(clinicId, verificationCode);
      setVerificationStatus('success');
      setVerificationMessage('Fax verified successfully!');
      if (clinicData) {
        setClinicData({ ...clinicData, fax_number_verified: true });
      }
      addToast({
        headerTitle: 'Fax number verified successfully!',
        type: ToastType.DEFAULT,
        variant: 'success',
        delay: 10000,
        autohide: true,
      });
    } catch (error: any) {
      setVerificationStatus('error');
      setVerificationMessage(
        error?.response?.data?.detail || error?.response?.data?.title || 'Invalid verification code.',
      );
    }
  };

  const resendCode = () => {
    sendVerificationCode();
  };

  // Create a placeholder clinic while loading
  const displayClinic =
    clinicData ||
    ({
      clinic_id: clinicId,
      name: clinicName,
      roles: [],
      phone: '',
      fax: '',
      address: { street_address: '', city: '', region: '', postal_code: '', country: '' },
    } as IClinic);

  if (isNatera) {
    return null;
  }

  return (
    <ExpandableSection
      id='manageOrganization'
      expandableContentId='manageOrganizationContent'
      defaultIsCollapsed
      headerTitle='Manage Organization'
    >
      {!clinicData && !isEditing ? (
        <div className='d-flex justify-content-center p-3'>
          <Spinner animation='border' variant='primary' />
        </div>
      ) : isEditing ? (
        <ClinicForm clinic={displayClinic} onSave={handleSave} onCancel={() => setIsEditing(false)} />
      ) : (
        <ClinicDetailsView
          clinic={displayClinic}
          onEdit={() => {
            setIsEditing(true);
            setVerificationStatus('idle');
            setVerificationMessage('');
          }}
          verificationStatus={verificationStatus}
          verificationMessage={verificationMessage}
          verificationCode={verificationCode}
          onVerificationCodeChange={setVerificationCode}
          onSubmitVerification={submitVerification}
          onResendCode={resendCode}
        />
      )}
    </ExpandableSection>
  );
}
